import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import {
  Cpu, Activity, Key, Wrench, FileText, Settings, RefreshCw,
  Copy, Trash2, Plus, Search, Shield, Server, Clock, Zap,
  BarChart3, Users, Filter, Eye, EyeOff, ChevronRight, Link2, Terminal, ExternalLink,
  Play, AlertTriangle, ChevronDown, Code2, Loader2
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const METHOD_COLORS = { GET: '#3B82F6', POST: '#10B981', PATCH: '#F59E0B', PUT: '#F59E0B', DELETE: '#EF4444' };
const ROLE_COLORS = { mcp_admin: '#0EA5E9', mcp_operator: '#8B5CF6', mcp_commercial: '#F59E0B', mcp_finance: '#10B981', mcp_readonly: '#6B7280' };
const PIE_COLORS = ['#0EA5E9', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'];

const MCPDashboard = () => {
  const [tab, setTab] = useState('overview');
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [tools, setTools] = useState([]);
  const [keys, setKeys] = useState([]);
  const [logs, setLogs] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const mcpFetch = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}/api/admin/mcp-proxy${path}`, {
      ...opts,
      headers: { 'Authorization': 'Bearer cookie-auth', 'Content-Type': 'application/json', ...opts.headers },
    });
    return res.json();
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statsR, toolsR, keysR, logsR, configR] = await Promise.all([
        mcpFetch('/stats'), mcpFetch('/tools'), mcpFetch('/api-keys'),
        mcpFetch('/audit/logs?limit=50'), mcpFetch('/config'),
      ]);
      if (statsR.success) setStats(statsR.data);
      if (toolsR.success) setTools(toolsR.data);
      if (keysR.success) setKeys(keysR.data);
      if (logsR.success) setLogs(logsR.data);
      if (configR.success) setConfig(configR.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [mcpFetch]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: Activity },
    { id: 'playground', label: 'Playground', icon: Play },
    { id: 'tools', label: 'Outils', icon: Wrench },
    { id: 'connect', label: 'Connexion', icon: Link2 },
    { id: 'analytics', label: 'Utilisation', icon: BarChart3 },
    { id: 'keys', label: 'Clés API', icon: Key },
    { id: 'logs', label: 'Journal', icon: FileText },
    { id: 'config', label: 'Configuration', icon: Settings },
  ];

  return (
    <div className="space-y-6" data-testid="mcp-dashboard">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-sky-50 border border-sky-200 rounded-lg">
            <Cpu className="w-7 h-7 text-sky-600" />
          </div>
          <div>
            <h1 className="text-lg md:text-2xl font-bold tracking-tight text-gray-900">MCP Dashboard</h1>
            <p className="text-sm text-gray-500">Mission Control Protocol — 40 outils, 10 catégories</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="mcp-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualiser
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex w-full overflow-x-auto h-auto p-1 bg-gray-100 rounded-xl">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.id} value={t.id} data-testid={`mcp-tab-${t.id}`}
                className="flex items-center gap-1.5 py-2.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg text-sm">
                <Icon className="w-4 h-4" /> <span className="hidden sm:inline">{t.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        <div className="mt-4">
          <TabsContent value="overview"><OverviewTab stats={stats} tools={tools} /></TabsContent>
          <TabsContent value="playground"><PlaygroundTab tools={tools} mcpFetch={mcpFetch} toast={toast} /></TabsContent>
          <TabsContent value="tools"><ToolsTab tools={tools} /></TabsContent>
          <TabsContent value="connect"><ConnectTab /></TabsContent>
          <TabsContent value="analytics"><AnalyticsTab stats={stats} /></TabsContent>
          <TabsContent value="keys"><KeysTab keys={keys} mcpFetch={mcpFetch} onRefresh={fetchAll} toast={toast} /></TabsContent>
          <TabsContent value="logs"><LogsTab logs={logs} mcpFetch={mcpFetch} /></TabsContent>
          <TabsContent value="config"><ConfigTab config={config} mcpFetch={mcpFetch} toast={toast} onRefresh={fetchAll} /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

// === OVERVIEW TAB ===
const OverviewTab = ({ stats, tools }) => {
  if (!stats) return <div className="text-center py-12 text-gray-400">Chargement...</div>;
  const o = stats.overview;
  const kpis = [
    { label: 'Outils MCP', value: o.total_tools, icon: Wrench, color: 'text-sky-600', bg: 'bg-sky-50' },
    { label: 'Clés API Actives', value: o.active_api_keys, icon: Key, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Requêtes aujourd\'hui', value: o.today_requests, icon: Zap, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Temps moyen', value: `${o.avg_response_ms}ms`, icon: Clock, color: 'text-purple-600', bg: 'bg-purple-50' },
  ];

  const catData = stats.categories ? Object.entries(stats.categories).map(([k, v]) => ({ name: k, value: v })) : [];

  return (
    <div className="space-y-4" data-testid="mcp-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <Card key={i} className="border" data-testid={`mcp-kpi-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{k.label}</p>
                    <p className="text-2xl font-bold mt-1">{k.value}</p>
                  </div>
                  <div className={`p-2 rounded-lg ${k.bg}`}><Icon className={`w-5 h-5 ${k.color}`} /></div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card className="border">
          <CardHeader className="pb-2"><CardTitle className="text-base">Requêtes (7 jours)</CardTitle></CardHeader>
          <CardContent>
            {stats.daily?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0EA5E9" radius={[4, 4, 0, 0]} name="Requêtes" />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-gray-400 text-center py-8">Pas de données</p>}
          </CardContent>
        </Card>

        <Card className="border">
          <CardHeader className="pb-2"><CardTitle className="text-base">Outils par catégorie</CardTitle></CardHeader>
          <CardContent className="flex items-center justify-center">
            {catData.length > 0 ? (
              <div className="flex items-center gap-6">
                <ResponsiveContainer width={160} height={160}>
                  <PieChart>
                    <Pie data={catData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2}>
                      {catData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1">
                  {catData.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-gray-600">{d.name}</span>
                      <span className="font-semibold">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-sm text-gray-400 py-8">Pas de données</p>}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card className="border">
          <CardHeader className="pb-2"><CardTitle className="text-base">Top outils (7j)</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-gray-50/50"><th className="text-left p-2 pl-4 font-medium text-xs text-gray-500">Outil</th><th className="text-right p-2 font-medium text-xs text-gray-500">Appels</th><th className="text-right p-2 pr-4 font-medium text-xs text-gray-500">Moy. ms</th></tr></thead>
              <tbody>
                {(stats.top_tools || []).map((t, i) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-gray-50/50">
                    <td className="p-2 pl-4 font-mono text-xs">{t.tool}</td>
                    <td className="p-2 text-right font-semibold">{t.count}</td>
                    <td className="p-2 pr-4 text-right text-gray-500">{t.avg_ms}ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card className="border">
          <CardHeader className="pb-2"><CardTitle className="text-base">Par rôle (7j)</CardTitle></CardHeader>
          <CardContent>
            {(stats.by_role || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={stats.by_role} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="role" type="category" tick={{ fontSize: 10 }} width={100} />
                  <Tooltip />
                  <Bar dataKey="count" name="Requêtes" radius={[0, 4, 4, 0]}>
                    {(stats.by_role || []).map((r, i) => <Cell key={i} fill={ROLE_COLORS[r.role] || '#6B7280'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-gray-400 text-center py-8">Pas de données</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};


// === PLAYGROUND TAB ===
const WRITE_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);
const CATEGORY_ICONS = {
  operations: '📦', clients: '👥', finance: '💰', contracts: '📋',
  communication: '💬', incidents: '🚨', drivers: '🚗', vehicles: '🚛',
  transporteurs: '🏢', audit: '📊',
};

const PlaygroundTab = ({ tools, mcpFetch, toast }) => {
  const [selectedTool, setSelectedTool] = useState(null);
  const [mcpTools, setMcpTools] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [result, setResult] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [elapsed, setElapsed] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [showConfirm, setShowConfirm] = useState(false);

  // Fetch full tool schemas from MCP transport on mount
  useEffect(() => {
    const fetchSchemas = async () => {
      try {
        const res = await fetch(`${API}/api/mcp/transport/mcp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json, text/event-stream' },
          body: JSON.stringify({ jsonrpc: '2.0', method: 'tools/list', id: 1 }),
        });
        const data = await res.json();
        if (data.result?.tools) setMcpTools(data.result.tools);
      } catch (e) { console.error('Failed to fetch MCP tool schemas', e); }
    };
    fetchSchemas();
  }, []);

  // Merge REST tools (category, method) with MCP transport tools (inputSchema)
  const mergedTools = React.useMemo(() => {
    return (tools || []).map(t => {
      const mcpT = mcpTools.find(m => m.name === t.name);
      return { ...t, inputSchema: mcpT?.inputSchema || { properties: {}, type: 'object' } };
    });
  }, [tools, mcpTools]);

  const categories = React.useMemo(() => {
    const cats = new Set(mergedTools.map(t => t.category));
    return ['all', ...Array.from(cats).sort()];
  }, [mergedTools]);

  const filtered = mergedTools.filter(t => {
    if (catFilter !== 'all' && t.category !== catFilter) return false;
    if (search && !t.name.toLowerCase().includes(search.toLowerCase()) &&
        !t.description.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const selectTool = (tool) => {
    setSelectedTool(tool);
    setResult(null);
    setElapsed(null);
    // Initialize form with defaults from schema
    const defaults = {};
    const props = tool.inputSchema?.properties || {};
    Object.entries(props).forEach(([key, schema]) => {
      if (schema.default !== undefined && schema.default !== null) {
        defaults[key] = schema.default;
      } else {
        defaults[key] = '';
      }
    });
    setFormValues(defaults);
  };

  const isWrite = selectedTool && WRITE_METHODS.has(selectedTool.method);

  const executeTool = async () => {
    if (!selectedTool) return;
    if (isWrite && !showConfirm) {
      setShowConfirm(true);
      return;
    }
    setShowConfirm(false);
    setExecuting(true);
    setResult(null);
    const t0 = performance.now();

    // Build arguments - strip empty strings, convert types
    const args = {};
    const props = selectedTool.inputSchema?.properties || {};
    Object.entries(formValues).forEach(([key, val]) => {
      if (val === '' || val === null || val === undefined) return;
      const schema = props[key] || {};
      const types = schema.anyOf ? schema.anyOf.map(a => a.type).filter(Boolean) : [schema.type];
      if (types.includes('integer') || types.includes('number')) {
        const num = Number(val);
        if (!isNaN(num)) { args[key] = num; return; }
      }
      if (types.includes('boolean')) {
        args[key] = val === 'true' || val === true;
        return;
      }
      args[key] = val;
    });

    try {
      const res = await fetch(`${API}/api/mcp/transport/mcp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json, text/event-stream' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'tools/call',
          params: { name: selectedTool.name, arguments: args },
          id: Date.now(),
        }),
      });
      const data = await res.json();
      setElapsed(Math.round(performance.now() - t0));

      if (data.result?.content) {
        const text = data.result.content.map(c => c.text).join('\n');
        try { setResult({ ok: !data.result.isError, data: JSON.parse(text) }); }
        catch { setResult({ ok: !data.result.isError, data: text }); }
      } else if (data.error) {
        setResult({ ok: false, data: data.error });
      }
    } catch (e) {
      setElapsed(Math.round(performance.now() - t0));
      setResult({ ok: false, data: { message: e.message } });
    }
    setExecuting(false);
  };

  const copyCurl = () => {
    if (!selectedTool) return;
    const args = {};
    Object.entries(formValues).forEach(([k, v]) => { if (v !== '' && v !== null) args[k] = v; });
    const curl = `curl -X POST "${API}/api/mcp/transport/mcp" \\\n  -H "Content-Type: application/json" \\\n  -H "Accept: application/json, text/event-stream" \\\n  -d '${JSON.stringify({ jsonrpc: '2.0', method: 'tools/call', params: { name: selectedTool.name, arguments: args }, id: 1 })}'`;
    navigator.clipboard.writeText(curl);
    toast({ title: 'Commande curl copiée', duration: 2000 });
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-280px)] min-h-[500px]" data-testid="mcp-playground">
      {/* Left panel - Tool selector */}
      <div className="w-80 flex-shrink-0 flex flex-col border rounded-xl bg-white overflow-hidden">
        <div className="p-3 border-b space-y-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Rechercher un outil..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm" data-testid="playground-search" />
          </div>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
            className="w-full h-8 text-xs border rounded-md px-2 bg-white text-gray-700" data-testid="playground-category-filter">
            {categories.map(c => (
              <option key={c} value={c}>{c === 'all' ? 'Toutes les catégories' : `${CATEGORY_ICONS[c] || '•'} ${c}`}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.map(t => (
            <button key={t.name} onClick={() => selectTool(t)}
              data-testid={`playground-tool-${t.name}`}
              className={`w-full text-left px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                selectedTool?.name === t.name ? 'bg-sky-50 border-l-2 border-l-sky-500' : ''
              }`}>
              <div className="flex items-center gap-2">
                <span className="text-xs">{CATEGORY_ICONS[t.category] || '•'}</span>
                <span className="font-mono text-xs font-medium text-gray-900 truncate">{t.name}</span>
                <Badge variant="outline" className={`ml-auto text-[10px] px-1.5 py-0 ${
                  WRITE_METHODS.has(t.method) ? 'border-amber-300 text-amber-600 bg-amber-50' : 'border-sky-200 text-sky-600'
                }`}>{t.method}</Badge>
              </div>
              <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-1">{t.description?.split('\n')[0]}</p>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">Aucun outil trouvé</div>
          )}
        </div>
        <div className="p-2 border-t bg-gray-50 text-center">
          <span className="text-xs text-gray-500">{filtered.length} / {mergedTools.length} outils</span>
        </div>
      </div>

      {/* Right panel - Form + Results */}
      <div className="flex-1 flex flex-col min-w-0">
        {!selectedTool ? (
          <div className="flex-1 flex items-center justify-center border rounded-xl bg-white">
            <div className="text-center text-gray-400 space-y-2">
              <Play className="w-10 h-10 mx-auto opacity-30" />
              <p className="text-sm">Sélectionnez un outil pour commencer</p>
            </div>
          </div>
        ) : (
          <>
            {/* Tool header */}
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-mono font-bold text-lg text-gray-900" data-testid="playground-tool-name">{selectedTool.name}</h3>
                  <Badge variant="outline" className={`text-xs ${
                    WRITE_METHODS.has(selectedTool.method) ? 'border-amber-300 text-amber-600 bg-amber-50' : 'border-sky-200 text-sky-600'
                  }`}>{selectedTool.method}</Badge>
                  <Badge variant="outline" className="text-xs">{selectedTool.category}</Badge>
                  {isWrite && (
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs gap-1">
                      <AlertTriangle className="w-3 h-3" /> Écriture
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{selectedTool.description}</p>
              </div>
              <Button variant="outline" size="sm" onClick={copyCurl} data-testid="playground-copy-curl">
                <Code2 className="w-3.5 h-3.5 mr-1.5" /> curl
              </Button>
            </div>

            {/* Form + Result split */}
            <div className="flex-1 grid grid-cols-2 gap-3 min-h-0">
              {/* Parameters form */}
              <div className="border rounded-xl bg-white overflow-hidden flex flex-col">
                <div className="px-4 py-2.5 border-b bg-gray-50 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Paramètres</span>
                  <span className="text-[10px] text-gray-400">
                    {Object.keys(selectedTool.inputSchema?.properties || {}).length} champs
                  </span>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  <ToolForm schema={selectedTool.inputSchema} values={formValues} onChange={setFormValues} />
                </div>
                <div className="p-3 border-t bg-gray-50">
                  {showConfirm ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-amber-700 text-xs bg-amber-50 p-2 rounded-lg">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        <span>Cet outil modifie des données. Confirmer l'exécution ?</span>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => setShowConfirm(false)}
                          data-testid="playground-cancel-btn">Annuler</Button>
                        <Button size="sm" className="flex-1 bg-amber-600 hover:bg-amber-700" onClick={executeTool}
                          data-testid="playground-confirm-btn">Confirmer</Button>
                      </div>
                    </div>
                  ) : (
                    <Button className="w-full bg-sky-600 hover:bg-sky-700" onClick={executeTool}
                      disabled={executing} data-testid="playground-execute-btn">
                      {executing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                      {executing ? 'Exécution...' : 'Exécuter'}
                    </Button>
                  )}
                </div>
              </div>

              {/* Result panel */}
              <div className="border rounded-xl bg-white overflow-hidden flex flex-col">
                <div className="px-4 py-2.5 border-b bg-gray-50 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Résultat</span>
                  <div className="flex items-center gap-2">
                    {elapsed !== null && <span className="text-[10px] text-gray-400">{elapsed}ms</span>}
                    {result && (
                      <Badge className={`text-[10px] ${result.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {result.ok ? 'Succès' : 'Erreur'}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex-1 overflow-auto p-0">
                  {result ? (
                    <div className="relative group">
                      <pre className="p-4 text-xs font-mono leading-relaxed whitespace-pre-wrap break-words text-gray-800"
                        data-testid="playground-result">
                        {typeof result.data === 'object' ? JSON.stringify(result.data, null, 2) : String(result.data)}
                      </pre>
                      <button onClick={() => {
                        navigator.clipboard.writeText(typeof result.data === 'object' ? JSON.stringify(result.data, null, 2) : String(result.data));
                        toast({ title: 'Résultat copié', duration: 1500 });
                      }} className="absolute top-2 right-2 p-1.5 bg-gray-100 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid="playground-copy-result">
                        <Copy className="w-3 h-3 text-gray-500" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-300">
                      <div className="text-center space-y-1">
                        <Terminal className="w-8 h-8 mx-auto opacity-40" />
                        <p className="text-xs">En attente d'exécution</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Dynamic form generator from MCP tool schema
const ToolForm = ({ schema, values, onChange }) => {
  const props = schema?.properties || {};
  const entries = Object.entries(props);

  if (entries.length === 0) {
    return <p className="text-sm text-gray-400 text-center py-4">Aucun paramètre requis</p>;
  }

  const getFieldType = (propSchema) => {
    const types = propSchema.anyOf
      ? propSchema.anyOf.map(a => a.type).filter(t => t && t !== 'null')
      : [propSchema.type];
    if (types.includes('integer') || types.includes('number')) return 'number';
    if (types.includes('boolean')) return 'boolean';
    return 'string';
  };

  const update = (key, val) => onChange({ ...values, [key]: val });

  return entries.map(([key, propSchema]) => {
    const fieldType = getFieldType(propSchema);
    const isOptional = propSchema.default !== undefined || (propSchema.anyOf && propSchema.anyOf.some(a => a.type === 'null'));
    const label = propSchema.title || key;

    return (
      <div key={key} className="space-y-1" data-testid={`playground-field-${key}`}>
        <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
          {label}
          {isOptional && <span className="text-gray-400 font-normal">(opt.)</span>}
          <span className="ml-auto text-[10px] text-gray-400 font-mono">{fieldType}</span>
        </label>
        {fieldType === 'boolean' ? (
          <select value={String(values[key] ?? '')} onChange={e => update(key, e.target.value)}
            className="w-full h-8 text-xs border rounded-md px-2 bg-white">
            <option value="">— Non défini —</option>
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        ) : (
          <Input
            type={fieldType === 'number' ? 'number' : 'text'}
            value={values[key] ?? ''}
            onChange={e => update(key, e.target.value)}
            placeholder={propSchema.default !== undefined && propSchema.default !== null ? `Défaut: ${propSchema.default}` : ''}
            className="h-8 text-xs"
          />
        )}
      </div>
    );
  });
};


// === TOOLS TAB ===
const ToolsTab = ({ tools }) => {
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');

  const categories = [...new Set(tools.map(t => t.category))].sort();
  const filtered = tools.filter(t => {
    if (catFilter !== 'all' && t.category !== catFilter) return false;
    if (search && !t.name.toLowerCase().includes(search.toLowerCase()) && !t.description.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="mcp-tools">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Rechercher un outil..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9" data-testid="mcp-tools-search" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-48" data-testid="mcp-tools-filter">
            <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Catégorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes ({tools.length})</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c} ({tools.filter(t => t.category === c).length})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50/50">
              <th className="text-left p-2 pl-4 font-medium text-xs text-gray-500 w-12">Méthode</th>
              <th className="text-left p-2 font-medium text-xs text-gray-500">Outil</th>
              <th className="text-left p-2 font-medium text-xs text-gray-500">Endpoint REST</th>
              <th className="text-left p-2 font-medium text-xs text-gray-500">Permission</th>
              <th className="text-left p-2 font-medium text-xs text-gray-500">Catégorie</th>
              <th className="text-left p-2 pr-4 font-medium text-xs text-gray-500">Description</th>
            </tr></thead>
            <tbody>
              {filtered.map((t, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-gray-50/50">
                  <td className="p-2 pl-4">
                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ backgroundColor: METHOD_COLORS[t.method] || '#6B7280' }}>{t.method}</span>
                  </td>
                  <td className="p-2 font-mono text-xs font-medium">{t.name}</td>
                  <td className="p-2 font-mono text-xs text-gray-500">/api/mcp/v1{t.rest_path}</td>
                  <td className="p-2"><Badge variant="outline" className="text-[10px] font-mono">{t.permission}</Badge></td>
                  <td className="p-2"><Badge variant="secondary" className="text-[10px]">{t.category}</Badge></td>
                  <td className="p-2 pr-4 text-xs text-gray-500 max-w-xs truncate">{t.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-center py-8 text-gray-400 text-sm">Aucun outil trouvé</p>}
        </CardContent>
      </Card>
      <p className="text-xs text-gray-400">{filtered.length} outil(s) affiché(s) sur {tools.length}</p>
    </div>
  );
};


// === CONNECT TAB ===
const CodeBlock = ({ children, label }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group" data-testid="code-block">
      {label && <div className="text-xs text-gray-400 mb-1 font-medium uppercase tracking-wider">{label}</div>}
      <pre className="bg-gray-950 text-gray-100 rounded-lg p-4 text-sm font-mono overflow-x-auto whitespace-pre-wrap leading-relaxed">
        {children}
      </pre>
      <button onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 bg-gray-800 hover:bg-gray-700 rounded text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid="copy-code-btn">
        {copied ? <span className="text-xs text-emerald-400">Copie!</span> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
};

const ConnectTab = () => {
  const mcpUrl = `${API}/api/mcp/transport/mcp`;
  const claudeConfig = JSON.stringify({
    mcpServers: {
      "baobab-connect": { url: mcpUrl }
    }
  }, null, 2);

  const curlExample = `curl -X POST "${mcpUrl}" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json, text/event-stream" \\
  -d '{
    "jsonrpc": "2.0",
    "method": "tools/list",
    "id": 1
  }'`;

  const curlToolCall = `curl -X POST "${mcpUrl}" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json, text/event-stream" \\
  -d '{
    "jsonrpc": "2.0",
    "method": "tools/call",
    "params": {
      "name": "search_operations",
      "arguments": {"limit": 5}
    },
    "id": 2
  }'`;

  return (
    <div className="space-y-6" data-testid="mcp-connect">
      <Card className="border-2 border-sky-200 bg-sky-50/30">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-sky-100 rounded-lg mt-0.5"><Link2 className="w-5 h-5 text-sky-600" /></div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900">Endpoint MCP Streamable HTTP</h3>
              <p className="text-sm text-gray-600 mt-1">Protocole MCP v2024-11-05 - 40 outils disponibles</p>
              <div className="mt-3 flex items-center gap-2">
                <code className="bg-white px-3 py-1.5 rounded-md border text-sm font-mono text-sky-700 break-all"
                  data-testid="mcp-endpoint-url">{mcpUrl}</code>
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(mcpUrl); }}
                  data-testid="copy-url-btn">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Actif</Badge>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-6">
        <Card className="border">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <Terminal className="w-4 h-4 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-base">Claude Desktop</CardTitle>
                <CardDescription className="text-xs">Configuration MCP native</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-xs text-gray-600 space-y-1">
              <p><strong>1.</strong> Ouvrez le fichier de configuration:</p>
              <p className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                macOS: ~/Library/Application Support/Claude/claude_desktop_config.json
              </p>
              <p className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                Windows: %APPDATA%\Claude\claude_desktop_config.json
              </p>
              <p className="mt-2"><strong>2.</strong> Collez cette configuration:</p>
            </div>
            <CodeBlock label="claude_desktop_config.json">{claudeConfig}</CodeBlock>
            <p className="text-xs text-gray-500"><strong>3.</strong> Redemarrez Claude Desktop</p>
          </CardContent>
        </Card>

        <Card className="border">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <ExternalLink className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-base">ChatGPT (Actions)</CardTitle>
                <CardDescription className="text-xs">Integration via GPT Builder</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-xs text-gray-600 space-y-1">
              <p><strong>1.</strong> GPT Builder &gt; Configure &gt; Actions</p>
              <p><strong>2.</strong> Cliquer "Create New Action"</p>
              <p><strong>3.</strong> Definir le Server URL:</p>
            </div>
            <CodeBlock label="Server URL">{mcpUrl}</CodeBlock>
            <div className="text-xs text-gray-600 mt-2 space-y-1">
              <p><strong>4.</strong> Methode: POST avec body JSON-RPC 2.0</p>
              <p><strong>5.</strong> Headers requis:</p>
            </div>
            <CodeBlock label="Headers">{`Content-Type: application/json
Accept: application/json, text/event-stream`}</CodeBlock>
          </CardContent>
        </Card>
      </div>

      <Card className="border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Terminal className="w-4 h-4 text-gray-500" /> Test avec curl
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <CodeBlock label="Lister les outils">{curlExample}</CodeBlock>
          <CodeBlock label="Appeler un outil">{curlToolCall}</CodeBlock>
        </CardContent>
      </Card>
    </div>
  );
};


// === ANALYTICS TAB ===
const AnalyticsTab = ({ stats }) => {
  if (!stats) return <div className="text-center py-12 text-gray-400">Chargement...</div>;

  return (
    <div className="space-y-4" data-testid="mcp-analytics">
      <div className="grid grid-cols-3 gap-4">
        <Card className="border col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-base">Requêtes quotidiennes (7 jours)</CardTitle></CardHeader>
          <CardContent>
            {stats.daily?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0EA5E9" radius={[4, 4, 0, 0]} name="Requêtes" />
                  <Bar dataKey="avg_ms" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Temps moy. (ms)" />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-gray-400 text-center py-12">Pas de données</p>}
          </CardContent>
        </Card>

        <Card className="border">
          <CardHeader className="pb-2"><CardTitle className="text-base">Répartition par rôle</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3 mt-2">
              {(stats.by_role || []).map((r, i) => {
                const total = stats.by_role.reduce((s, x) => s + x.count, 0);
                const pct = total > 0 ? Math.round(r.count / total * 100) : 0;
                return (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium">{r.role}</span>
                      <span className="text-gray-500">{r.count} ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: ROLE_COLORS[r.role] || '#6B7280' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border">
        <CardHeader className="pb-2"><CardTitle className="text-base">Top 10 outils les plus utilisés (7 jours)</CardTitle></CardHeader>
        <CardContent>
          {(stats.top_tools || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.top_tools} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="tool" type="category" tick={{ fontSize: 10 }} width={180} />
                <Tooltip />
                <Bar dataKey="count" fill="#10B981" radius={[0, 4, 4, 0]} name="Appels" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-gray-400 text-center py-12">Pas de données</p>}
        </CardContent>
      </Card>
    </div>
  );
};

// === KEYS TAB ===
const KeysTab = ({ keys, mcpFetch, onRefresh, toast }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newRole, setNewRole] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [creating, setCreating] = useState(false);

  const createKey = async () => {
    if (!newLabel || !newRole) return;
    setCreating(true);
    try {
      const r = await mcpFetch('/api-keys', { method: 'POST', body: JSON.stringify({ label: newLabel, role: newRole }) });
      if (r.success) {
        setNewKey(r.data);
        toast({ title: 'Clé créée', description: `Clé ${newLabel} créée avec le rôle ${newRole}` });
        onRefresh();
      } else {
        toast({ title: 'Erreur', description: r.detail || 'Erreur de création', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Erreur', description: e.message, variant: 'destructive' });
    }
    setCreating(false);
  };

  const revokeKey = async (keyId, label) => {
    if (!window.confirm(`Désactiver la clé "${label}" ?`)) return;
    const r = await mcpFetch(`/api-keys/${keyId}`, { method: 'DELETE' });
    if (r.success) {
      toast({ title: 'Clé désactivée', description: label });
      onRefresh();
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copié', description: 'Clé copiée dans le presse-papiers' });
  };

  return (
    <div className="space-y-4" data-testid="mcp-keys">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Clés API MCP</h3>
          <p className="text-sm text-gray-500">{keys.length} clé(s) enregistrée(s)</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) { setNewKey(null); setNewLabel(''); setNewRole(''); } }}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="mcp-create-key-btn"><Plus className="w-4 h-4 mr-2" /> Nouvelle clé</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{newKey ? 'Clé créée' : 'Créer une clé API MCP'}</DialogTitle>
              <DialogDescription>{newKey ? 'Copiez cette clé maintenant, elle ne sera plus affichée.' : 'Attribuez un label et un rôle.'}</DialogDescription>
            </DialogHeader>
            {newKey ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <code className="text-sm font-mono flex-1 break-all">{newKey.key}</code>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(newKey.key)}><Copy className="w-4 h-4" /></Button>
                </div>
                <p className="text-xs text-gray-500">Rôle: <Badge variant="secondary">{newKey.role}</Badge></p>
              </div>
            ) : (
              <div className="space-y-4">
                <div><Label>Label</Label><Input placeholder="Ex: Agent Claude Production" value={newLabel} onChange={e => setNewLabel(e.target.value)} data-testid="mcp-key-label" /></div>
                <div>
                  <Label>Rôle</Label>
                  <Select value={newRole} onValueChange={setNewRole}>
                    <SelectTrigger data-testid="mcp-key-role"><SelectValue placeholder="Choisir un rôle" /></SelectTrigger>
                    <SelectContent>
                      {['mcp_admin', 'mcp_operator', 'mcp_commercial', 'mcp_finance', 'mcp_readonly'].map(r => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <DialogFooter>
              {newKey ? (
                <Button onClick={() => { setShowDialog(false); setNewKey(null); }}>Fermer</Button>
              ) : (
                <Button onClick={createKey} disabled={!newLabel || !newRole || creating} data-testid="mcp-key-submit">
                  {creating ? 'Création...' : 'Créer'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50/50">
              <th className="text-left p-2 pl-4 font-medium text-xs text-gray-500">Label</th>
              <th className="text-left p-2 font-medium text-xs text-gray-500">Rôle</th>
              <th className="text-left p-2 font-medium text-xs text-gray-500">Préfixe</th>
              <th className="text-left p-2 font-medium text-xs text-gray-500">Statut</th>
              <th className="text-left p-2 font-medium text-xs text-gray-500">Créée le</th>
              <th className="text-right p-2 pr-4 font-medium text-xs text-gray-500">Actions</th>
            </tr></thead>
            <tbody>
              {keys.map((k, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-gray-50/50">
                  <td className="p-2 pl-4 font-medium">{k.label}</td>
                  <td className="p-2"><Badge style={{ backgroundColor: ROLE_COLORS[k.role] || '#6B7280', color: '#fff', border: 'none' }} className="text-[10px]">{k.role}</Badge></td>
                  <td className="p-2 font-mono text-xs text-gray-500">{k.id?.slice(0, 12)}...</td>
                  <td className="p-2">{k.is_active !== false ? <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50 text-[10px]">Actif</Badge> : <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50 text-[10px]">Inactif</Badge>}</td>
                  <td className="p-2 text-xs text-gray-500">{k.created_at ? new Date(k.created_at).toLocaleDateString('fr-FR') : '-'}</td>
                  <td className="p-2 pr-4 text-right">
                    {k.is_active !== false && (
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 h-7" onClick={() => revokeKey(k.id, k.label)} data-testid={`mcp-revoke-key-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {keys.length === 0 && <p className="text-center py-8 text-gray-400 text-sm">Aucune clé API</p>}
        </CardContent>
      </Card>
    </div>
  );
};

// === LOGS TAB ===
const LogsTab = ({ logs, mcpFetch }) => {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const items = logs?.logs || [];
  const filtered = items.filter(l => {
    if (roleFilter !== 'all' && l.role !== roleFilter) return false;
    if (search && !l.action?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="mcp-logs">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Rechercher par outil..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="mcp-logs-search" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-48" data-testid="mcp-logs-role-filter"><Users className="w-4 h-4 mr-2" /><SelectValue placeholder="Rôle" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les rôles</SelectItem>
            {['mcp_admin', 'mcp_operator', 'mcp_commercial', 'mcp_finance', 'mcp_readonly'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50/50">
              <th className="text-left p-2 pl-4 font-medium text-xs text-gray-500 w-40">Date</th>
              <th className="text-left p-2 font-medium text-xs text-gray-500">Outil</th>
              <th className="text-left p-2 font-medium text-xs text-gray-500">Rôle</th>
              <th className="text-left p-2 font-medium text-xs text-gray-500">Label</th>
              <th className="text-right p-2 font-medium text-xs text-gray-500">Résultats</th>
              <th className="text-right p-2 pr-4 font-medium text-xs text-gray-500">Temps</th>
            </tr></thead>
            <tbody>
              {filtered.slice(0, 50).map((l, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-gray-50/50">
                  <td className="p-2 pl-4 text-xs text-gray-500 font-mono">{l.date ? new Date(l.date).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}</td>
                  <td className="p-2 font-mono text-xs font-medium">{l.action}</td>
                  <td className="p-2"><Badge style={{ backgroundColor: ROLE_COLORS[l.role] || '#6B7280', color: '#fff', border: 'none' }} className="text-[10px]">{l.role}</Badge></td>
                  <td className="p-2 text-xs text-gray-500">{l.label}</td>
                  <td className="p-2 text-right text-xs">{l.resultats ?? '-'}</td>
                  <td className="p-2 pr-4 text-right text-xs font-mono">{l.temps_ms != null ? `${l.temps_ms}ms` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-center py-8 text-gray-400 text-sm">Aucun log trouvé</p>}
        </CardContent>
      </Card>
      <p className="text-xs text-gray-400">{filtered.length} entrée(s) — Total: {logs?.stats?.total_requetes || 0} requêtes MCP</p>
    </div>
  );
};

// === CONFIG TAB ===
const ConfigTab = ({ config, mcpFetch, toast, onRefresh }) => {
  const [saving, setSaving] = useState(false);
  const [rl, setRl] = useState({ enabled: false, default_rpm: 60, burst: 10 });
  const [transports, setTransports] = useState({ rest: true, stdio: true, sse: false });

  useEffect(() => {
    if (config) {
      setRl(config.rate_limiting || { enabled: false, default_rpm: 60, burst: 10 });
      setTransports(config.transports || { rest: true, stdio: true, sse: false });
    }
  }, [config]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const r = await mcpFetch('/config', { method: 'PUT', body: JSON.stringify({ rate_limiting: rl, transports }) });
      if (r.success) {
        toast({ title: 'Sauvegardé', description: 'Configuration MCP mise à jour' });
        onRefresh();
      }
    } catch (e) {
      toast({ title: 'Erreur', description: e.message, variant: 'destructive' });
    }
    setSaving(false);
  };

  const roles = [
    { name: 'mcp_admin', desc: 'Accès total', perms: 'read:* write:*' },
    { name: 'mcp_operator', desc: 'Opérations & Logistique', perms: 'read:operations write:operations read:drivers read:vehicles read:kpis' },
    { name: 'mcp_commercial', desc: 'Clients & Ventes', perms: 'read/write:operations read/write:clients read:finances read:kpis read:contracts read/write:messages' },
    { name: 'mcp_finance', desc: 'Finance & Comptabilité', perms: 'read/write:finances read:kpis read:clients read:contracts' },
    { name: 'mcp_readonly', desc: 'Lecture seule', perms: 'read:*' },
  ];

  return (
    <div className="space-y-4" data-testid="mcp-config">
      <div className="grid grid-cols-2 gap-4">
        <Card className="border">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Server className="w-4 h-4" /> Serveur MCP</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <span className="text-gray-500">Nom</span><span className="font-mono">{config?.server?.name}</span>
              <span className="text-gray-500">Version</span><span className="font-mono">{config?.server?.version}</span>
              <span className="text-gray-500">Outils</span><span className="font-semibold">{config?.server?.tools_count}</span>
              <span className="text-gray-500">Rôles</span><span>{config?.roles?.length}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Zap className="w-4 h-4" /> Rate Limiting</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Activé</Label>
              <Switch checked={rl.enabled} onCheckedChange={v => setRl({ ...rl, enabled: v })} data-testid="mcp-rl-toggle" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-gray-500">Requêtes/min</Label><Input type="number" value={rl.default_rpm} onChange={e => setRl({ ...rl, default_rpm: +e.target.value })} data-testid="mcp-rl-rpm" /></div>
              <div><Label className="text-xs text-gray-500">Burst</Label><Input type="number" value={rl.burst} onChange={e => setRl({ ...rl, burst: +e.target.value })} data-testid="mcp-rl-burst" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4" /> Modes de transport</CardTitle>
          <CardDescription>Protocoles de communication MCP activés</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-6">
            {[
              { key: 'rest', label: 'REST API', desc: 'HTTP JSON standard' },
              { key: 'stdio', label: 'stdio', desc: 'Claude Desktop, scripts' },
              { key: 'sse', label: 'SSE', desc: 'Server-Sent Events (agents cloud)' },
            ].map(t => (
              <div key={t.key} className="flex items-center gap-3 p-3 border rounded-lg flex-1">
                <Switch checked={transports[t.key]} onCheckedChange={v => setTransports({ ...transports, [t.key]: v })} data-testid={`mcp-transport-${t.key}`} />
                <div><p className="text-sm font-medium">{t.label}</p><p className="text-xs text-gray-500">{t.desc}</p></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4" /> Matrice des permissions RBAC</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50/50">
              <th className="text-left p-2 pl-4 font-medium text-xs text-gray-500">Rôle</th>
              <th className="text-left p-2 font-medium text-xs text-gray-500">Description</th>
              <th className="text-left p-2 pr-4 font-medium text-xs text-gray-500">Permissions</th>
            </tr></thead>
            <tbody>
              {roles.map((r, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="p-2 pl-4"><Badge style={{ backgroundColor: ROLE_COLORS[r.name] || '#6B7280', color: '#fff', border: 'none' }} className="text-[10px]">{r.name}</Badge></td>
                  <td className="p-2 text-xs text-gray-600">{r.desc}</td>
                  <td className="p-2 pr-4"><div className="flex flex-wrap gap-1">{r.perms.split(' ').map((p, j) => <Badge key={j} variant="outline" className="text-[10px] font-mono">{p}</Badge>)}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={saveConfig} disabled={saving} data-testid="mcp-save-config">
          {saving ? 'Sauvegarde...' : 'Sauvegarder la configuration'}
        </Button>
      </div>
    </div>
  );
};

export default MCPDashboard;
